import { useEffect, useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';

import { trpc, TrpcOutputs } from '../../config/trpc';

type DocumentOutput = TrpcOutputs['getDocumentsByRequestId'];
type UseDocuments = [DocumentOutput, boolean, string?];

/**
 *
 * @param requestId - The ID of the request to fetch documents for.
 * @param localName - The name of the local form field to watch for documents when requestId is not provided.
 * @description A hook to fetch and manage documents related to a specific request. It supports either real-time DB subscriptions or local form state.
 * @returns An array containing the documents, loading state, and an optional error message.
 */
export const useDocuments = ({ requestId, localName }: { requestId?: number; localName?: string }): UseDocuments => {
  const [documentsFromDB, setDocumentsFromDB] = useState<DocumentOutput>([]);
  const { setValue } = useFormContext();
  const documentsFromLocal = useWatch({ name: localName });
  const documents = (requestId ? documentsFromDB : documentsFromLocal) || [];

  const { data, isLoading, error } = trpc.getDocumentsByRequestId.useQuery(
    { request_id: requestId },
    { enabled: !!requestId },
  );

  useEffect(() => {
    if (!requestId && !localName) {
      console.warn('useDocuments hook requires either requestId or localName to function properly.');
    }
  }, [requestId, localName]);

  useEffect(() => {
    if (data?.length) {
      setDocumentsFromDB(data);
      if (localName) setValue(localName, data);
    }
  }, [data, localName, setValue]);

  trpc.onDocumentCreated.useSubscription(
    { requestId },
    {
      onData: (data) => {
        setDocumentsFromDB((prev) => [...prev, data]);
      },
      onError: (error) => {
        console.log('onDocumentCreated.error: ', error);
      },
      enabled: !!requestId,
    },
  );

  trpc.onDocumentDeleted.useSubscription(
    { requestId },
    {
      onData: (deletedId) => {
        setDocumentsFromDB((prev) => prev.filter((doc) => doc?.id !== deletedId));
      },
      onError: (error) => {
        console.log('onDocumentDeleted.error: ', error);
      },
      enabled: !!requestId,
    },
  );

  trpc.onDocumentUpdated.useSubscription(
    { requestId },
    {
      onData: (data) => {
        const documentToUpdate = documentsFromDB.find((doc) => doc.id === data.id);
        if (documentToUpdate) {
          setDocumentsFromDB((prev) => prev.map((doc) => (doc.id === data.id ? { ...doc, ...data } : doc)));
        }
      },
      onError: (error) => {
        console.log('onDocumentUpdated.error: ', error);
      },
      enabled: !!requestId,
    },
  );

  return [documents, isLoading, error?.message];
};
